import { Controller } from "@hotwired/stimulus";
import { gsap } from "gsap";
import { whenStageActive } from "./utils/when_stage_active";

// Wraps an image and a sibling skeleton placeholder.
// Hides the skeleton and crossfades the image when
// it finishes loading AND the closest audit stage is
// active. The image still loads in the background
// while the stage is hidden: only the visual
// crossfade is deferred.
//
// Why deferred play: the audit show page renders all
// stages up front (hidden by CSS). Without
// `whenStageActive`, the crossfade would run while
// the stage is `display: none`, ending before the
// user sees it.
//
// Markup contract:
//   <div data-controller="audit-image-load">
//     <div data-audit-image-load-target="skeleton">
//       <!-- shimmer skeleton -->
//     </div>
//     <img data-audit-image-load-target="image"
//          src="..."
//          class="opacity-0"/>
//   </div>
//
// Honors prefers-reduced-motion: still hides
// skeleton and shows image, just without the fade.
export default class extends Controller {
  static targets = ["skeleton", "image"];
  static values = {
    duration: { type: Number, default: 0.4 },
  };

  connect() {
    if (!this.hasImageTarget) return;

    this.imageReady = false;
    this.stageActive = false;

    const img = this.imageTarget;
    if (img.complete && img.naturalWidth > 0) {
      this.imageReady = true;
    } else {
      this.boundOnLoad = () => {
        this.imageReady = true;
        this.maybeReveal();
      };
      this.boundFail = () => {
        this.fail();
      };
      img.addEventListener("load", this.boundOnLoad);
      img.addEventListener("error", this.boundFail);

      // Safety net: a stuck image (slow CDN, blocked
      // request) shouldn't keep the skeleton up
      // forever. After a generous timeout, treat it
      // as failed. The skeleton stays visible, same
      // as the explicit error path.
      this.timeoutId = setTimeout(() => {
        if (this.imageReady) return;
        this.fail();
      }, 8_000);
    }

    this.cancelWait = whenStageActive(
      this.element,
      () => {
        this.stageActive = true;
        this.maybeReveal();
      }
    );
  }

  disconnect() {
    if (this.hasImageTarget) {
      if (this.boundOnLoad) {
        this.imageTarget.removeEventListener(
          "load",
          this.boundOnLoad
        );
      }
      if (this.boundFail) {
        this.imageTarget.removeEventListener(
          "error",
          this.boundFail
        );
      }
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.cancelWait?.();
    this.tween?.kill();
  }

  maybeReveal() {
    if (!this.imageReady) return;
    if (!this.stageActive) return;
    this.reveal();
  }

  reveal() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduced) {
      if (this.hasSkeletonTarget) {
        this.skeletonTarget.style.display = "none";
      }
      gsap.set(this.imageTarget, { autoAlpha: 1 });
      return;
    }

    const tl = gsap.timeline();
    tl.to(this.imageTarget, {
      autoAlpha: 1,
      duration: this.durationValue,
      ease: "power2.out",
    });
    if (this.hasSkeletonTarget) {
      tl.to(
        this.skeletonTarget,
        {
          autoAlpha: 0,
          duration: this.durationValue,
          ease: "power2.out",
          onComplete: () => {
            this.skeletonTarget.style.display = "none";
          },
        },
        "<"
      );
    }
    this.tween = tl;
  }

  fail() {
    // Image errored: keep the skeleton visible
    // (it's better than a broken-image icon). The
    // step's terminal-empty path will pick this up
    // on the next render.
    if (this.hasSkeletonTarget) {
      this.skeletonTarget.style.display = "";
    }
  }
}
